import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Divider } from "@mui/material";

import { SiteByIdDTO, deActivateEstimateById, deleteSiteById, getSiteById } from "api";
import { QUERY_KEYS } from "consts";
import { useGetUsersDataProviderFn, useNotify } from "hooks";
import { getLoggedUserData } from "utils";
import { AllowedFormValues } from "types";
import { DeleteDialog, Loading, useDialogControls } from "components";

import { SITES_PAGE } from "../../consts";
import { SiteNameDetail } from "../site-delete-dialog-name";

import { ParameterBasedEstimation } from "./parameter-based-estimation";
import { UtilityProviders } from "./utility-providers";
import { DataProviders } from "./data-providers/data-providers";

export const SiteEditorForm: FunctionComponent<{
  allowedValues: AllowedFormValues;
  site: SiteByIdDTO;
  toDeleteSite: boolean;
}> = ({ allowedValues, site, toDeleteSite }) => {
  // ---- Hooks ----
  const notify = useNotify();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const deleteDialogControls = useDialogControls();
  const { superadmin } = getLoggedUserData();
  const { data: users, isLoading: areUsersLoading } = useGetUsersDataProviderFn();

  // ---- State ----
  const [siteData, setSiteData] = useState<SiteByIdDTO | undefined>(site);

  // ---- API Calls ----
  const { mutateAsync: deleteSiteRequest } = useMutation(deleteSiteById);
  const { mutateAsync: deleteEstimateRequest } = useMutation(deActivateEstimateById);

  const allowedUsers = useMemo(() => (users?.filter(
    (currentUser) =>
      currentUser.active &&
      currentUser.admin &&
      !currentUser.auditor &&
      !site?.users?.map((siteUser) => siteUser.id).includes(currentUser.id),
  ) || []), [users, site]);

  const onDataUpdate = (data: any) => setSiteData(data);

  const onDisableEstimate = async (EstimateId: number | undefined) => {
    if (siteData?.id && EstimateId) {
      await deleteEstimateRequest(EstimateId, {
        onError: () => {
          deleteDialogControls.close();
          notify.error(`Some error has happen while de-activating Estimate!`);
        },
        onSuccess: () => {
          deleteDialogControls.close();
          notify.success(`Estimate successfully de-activated!`);
          queryClient.invalidateQueries([QUERY_KEYS.SITES]);
          queryClient.invalidateQueries([
            QUERY_KEYS.ESTIMATES,
            QUERY_KEYS.SITES,
            site?.id,
          ]);
          if (site) {
            getSiteById(site?.id)
              .then((siteDetails) => {
                setSiteData(siteDetails);
              })
              .catch((error) => {
                // eslint-disable-next-line
                console.error("Error fetching site data:", error);
              });
          }
        },
      });
    }
  };

  const onDeleteClick = async () => {
    if (siteData?.id) {
      try {
        await deleteSiteRequest(siteData.id, {
          onSuccess: () => {
            deleteDialogControls.close();
            notify.success(`Site successfully deleted: ${siteData.name}!`);
            queryClient.invalidateQueries([QUERY_KEYS.SITES]);
            navigate(SITES_PAGE.ABSOLUTE_PATH);
            window.sessionStorage.removeItem('expanded-sites');
          },
        });
      } catch (error: any) {
        notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : `Some error has happen while deleting site: ${siteData.name}!`);
        deleteDialogControls.close();
      }
    }
  };

  useEffect(() => {
    setSiteData(site);
  }, [site]);

  useEffect(() => {
    if (siteData) window.localStorage.setItem("Site", JSON.stringify(siteData));
  }, [siteData]);

  return (
    <>
      {areUsersLoading
        ? <Loading />
        : (<>
          <Divider sx={{ mt: "45px", borderColor: "#d9d9d9" }} />
          <ParameterBasedEstimation
            siteData={siteData || ({} as SiteByIdDTO)}
            allowedValues={allowedValues}
            onDisableEstimate={onDisableEstimate}
            onEstimatesDataUpdate={onDataUpdate}
          />
          <Divider sx={{ mt: 3, borderColor: "#d9d9d9" }} />
          <UtilityProviders
            siteData={siteData || ({} as SiteByIdDTO)}
            site={site || ({} as SiteByIdDTO)}
            allowedValues={allowedValues}
            onCollectorDataUpdate={onDataUpdate}
            setSiteData={setSiteData}
            isSuperAdmin={superadmin}
          />
          <Divider sx={{ mt: 3, borderColor: "#d9d9d9" }} />
          <DataProviders
            siteData={siteData || ({} as SiteByIdDTO)}
            allowedUsers={allowedUsers}
          />

          <DeleteDialog
            isOpen={deleteDialogControls.isOpen}
            onCancel={deleteDialogControls.close}
            onDeleteClick={toDeleteSite ? onDeleteClick : () => { }}
            target={toDeleteSite ? <SiteNameDetail name={siteData?.name} /> : ""}
            title={toDeleteSite ? "Delete Site?" : ""}
            confirmTitle="De-activate"
          />
        </>)
      }
    </>
  );
};
